import React from "react";
import { useTranslation } from "react-i18next";

import { UseCases } from "@planzone/core";
import { Safeguard } from "@scenes/safeguard";
import { Identifiable, Group as G } from "@planzone/dto";
import {
  useProjectResourcesRetrieval,
  useRetrieveCurrentPlanzone,
  useRetrieveResourceGroups,
} from "@planzone/view-models";
import { Skeleton } from "@common/skeleton";
import { Group } from "./group";
import { ResourceTypesFilter } from "./resourceTypesFilter";
import { AddGroupIcon } from "./addGroupIcon";
import { Props } from "./types";
import styles from "./styles.scss";

function SideBar({ project, filter, setFilter, createProjectGroup }: Props) {
  const { t } = useTranslation();
  const { planzone } = useRetrieveCurrentPlanzone();
  const {
    isRequestPending: isPlanzoneRequestPending,
    isRequestSuccess: isPlanzoneRequestSuccess,
    isRequestFailure: isPlanzoneRequestFailure,
    groups,
    retrieveResourceGroups: retrievePlanzoneResourceGroups,
  } = useRetrieveResourceGroups(planzone.uuid);
  const {
    isRequestPending: isProjectRequestPending,
    isRequestSuccess: isProjectRequestSuccess,
    isRequestFailure: isProjectRequestFailure,
    groups: projectGroups,
    retrieveResourceGroups: retrieveProjectResourceGroups,
  } = useRetrieveResourceGroups(project.uuid);
  const { projectResources } = useProjectResourcesRetrieval(project);
  const planzoneGroups = groups.reduce<Identifiable<G>[]>(
    (filteredGroups, group) => {
      const filteredGroup = {
        ...group,
        resources: group.resources.filter(
          ({ uuid }) =>
            !!projectResources.find(({ resource }) => resource.uuid === uuid),
        ),
      };

      return filteredGroup.resources.length > 0
        ? [...filteredGroups, filteredGroup]
        : filteredGroups;
    },
    [],
  );

  React.useEffect(() => {
    !isPlanzoneRequestPending &&
      !isPlanzoneRequestSuccess &&
      !isPlanzoneRequestFailure &&
      retrievePlanzoneResourceGroups({
        namespace: planzone.uuid,
        resourceType: "planzone",
      });
  }, []);

  React.useEffect(() => {
    !isProjectRequestPending &&
      !isProjectRequestSuccess &&
      !isProjectRequestFailure &&
      retrieveProjectResourceGroups({
        namespace: project.uuid,
        resourceType: "project",
      });
  }, []);

  return (
    <div className={styles.sidebar}>
      {!isPlanzoneRequestPending && planzoneGroups.length > 0 && (
        <>
          <div className={styles.header}>
            <h4 className={styles.title}>{t("membersList.planzoneTeams")}</h4>
          </div>
          <ul className={styles.groups}>
            {planzoneGroups.map((group) => (
              <li key={group.uuid} className={styles.group}>
                <Group
                  group={group}
                  project={project}
                  isSelected={
                    !!filter.groups &&
                    !!filter.groups.find(({ uuid }) => uuid === group.uuid)
                  }
                  select={() =>
                    setFilter({
                      ...filter,
                      groups: filter.groups
                        ? [...filter.groups, group]
                        : [group],
                      areSortedByGroups: false,
                    })
                  }
                  unSelect={() =>
                    setFilter({
                      ...filter,
                      groups: filter.groups
                        ? filter.groups.filter(
                            ({ uuid }) => uuid !== group.uuid,
                          )
                        : filter.groups,
                    })
                  }
                />
              </li>
            ))}
          </ul>
        </>
      )}
      <div className={styles.header}>
        <h4 className={styles.title}>{t("membersList.projectTeams")}</h4>
        <Safeguard useCases={[UseCases.addResourceGroup]}>
          {({ canAddResourceGroup }) =>
            canAddResourceGroup && (
              <div onClick={createProjectGroup}>
                <AddGroupIcon className={styles.icon} />
              </div>
            )
          }
        </Safeguard>
      </div>
      {!isProjectRequestPending ? (
        projectGroups.length > 0 ? (
          <ul className={styles.groups}>
            {projectGroups.map((group) => (
              <li key={group.uuid} className={styles.group}>
                <Group
                  group={group}
                  project={project}
                  isSelected={
                    !!filter.groups &&
                    !!filter.groups.find(({ uuid }) => uuid === group.uuid)
                  }
                  select={() =>
                    setFilter({
                      ...filter,
                      groups: filter.groups
                        ? [...filter.groups, group]
                        : [group],
                      areSortedByGroups: false,
                    })
                  }
                  unSelect={() =>
                    setFilter({
                      ...filter,
                      groups: filter.groups
                        ? filter.groups.filter(
                            ({ uuid }) => uuid !== group.uuid,
                          )
                        : filter.groups,
                    })
                  }
                />
              </li>
            ))}
          </ul>
        ) : (
          <div className={styles.groupPlaceholder} onClick={createProjectGroup}>
            <p>{t("membersList.groupPlaceholder")}</p>
          </div>
        )
      ) : (
        <div className={styles.loader}>
          <div className={styles.itemLoader}>
            <Skeleton className={styles.iconSkeleton} />
            <Skeleton className={styles.groupSkeleton} />
          </div>
          <div className={styles.itemLoader}>
            <Skeleton className={styles.iconSkeleton} />
            <Skeleton className={styles.groupSkeleton} />
          </div>
          <div className={styles.itemLoader}>
            <Skeleton className={styles.iconSkeleton} />
            <Skeleton className={styles.groupSkeleton} />
          </div>
        </div>
      )}
      <div className={styles.header}>
        <h4 className={styles.title}>
          {t("filterProjectResource.sideBarName")}
        </h4>
      </div>
      <ResourceTypesFilter
        filters={filter.resourceTypes}
        inactiveOnly={filter.inactiveOnly}
        activeOnly={!!filter.activeOnly}
        setFilters={(resourceTypes) => setFilter({ ...filter, resourceTypes })}
        // The activated and the deactivated resources exclude each other.
        setInactiveOnly={(inactiveOnly) =>
          setFilter({
            ...filter,
            inactiveOnly,
            activeOnly: inactiveOnly ? false : filter.activeOnly,
          })
        }
        setActiveOnly={(activeOnly) =>
          setFilter({
            ...filter,
            activeOnly,
            inactiveOnly: activeOnly ? false : filter.inactiveOnly,
          })
        }
      />
    </div>
  );
}

export { SideBar };
